import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Form, Button, Spinner, Alert } from 'react-bootstrap';
import { FiSave, FiUser } from 'react-icons/fi';
import InfoTooltip from '@core/components/InfoTooltip';
import { sendRequest } from '@infrastructure/api/api';
import { useUserProfile } from '../hooks/useUserProfile';
import { updateUserProfile } from '../api/profileApi';
import { UpdateUserProfileRequest, UserProfile } from '../types/profile';
import { toDateOnlyInputValue } from '@core/utils/formatDateOnly';
import { SESSION_STORAGE_KEYS } from '@features/auth/constants/sessionStorageKeys';

type TitleOption = {
  title_code: string;
  title_name: string;
};

type UserProfileFormData = UpdateUserProfileRequest &
  Pick<UserProfile, 'email' | 'trn'>;

const genderMap: Record<string, string> = {
  Male: 'M',
  Female: 'F',
  Other: 'O',
  M: 'M',
  F: 'F',
  O: 'O',
};

const UserProfilePage: React.FC = () => {
  const { profile, loading, error: profileError, refetch } = useUserProfile();
  const [saving, setSaving] = useState(false);
  const [titlesLoading, setTitlesLoading] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [titles, setTitles] = useState<TitleOption[]>([]);
  const [formData, setFormData] = useState<Partial<UserProfileFormData>>({});

  useEffect(() => {
    if (!profile) return;

    setFormData({
      fname: profile.fname ?? '',
      lname: profile.lname ?? '',
      title_code: profile.title_code ?? '',
      email: profile.email ?? '',
      phone1_no: profile.phone1_no ?? '',
      trn: profile.trn ?? '',
      date_of_birth: profile.date_of_birth ?? '',
      gender: genderMap[profile.gender] || profile.gender || '',
    });
  }, [profile]);

  useEffect(() => {
    let isCurrent = true;

    const loadTitles = async () => {
      setTitlesLoading(true);
      try {
        const response = await sendRequest<any, any>('GET', 'user/title/query?all=true');
        if (!isCurrent || !response.isSuccess()) return;

        const data = response.getData();
        const list = data?.data ?? data ?? [];
        setTitles(Array.isArray(list) ? list : []);
      } finally {
        if (isCurrent) {
          setTitlesLoading(false);
        }
      }
    };

    void loadTitles();

    return () => {
      isCurrent = false;
    };
  }, []);

  const handleChange = (
    field: keyof UpdateUserProfileRequest | 'email' | 'trn',
    value: string
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setPageError(null);
    setSuccess(null);

    try {
      const payload: UpdateUserProfileRequest = {
        fname: formData.fname ?? null,
        lname: formData.lname ?? null,
        title_code: formData.title_code ?? null,
        date_of_birth: formData.date_of_birth ?? null,
        gender: formData.gender ?? null,
        phone1_no: formData.phone1_no ?? null,
      };

      await updateUserProfile(payload);

      const selectedTitle = titles.find(
        (title) => title.title_code === (formData.title_code ?? '')
      );
      const parts = [
        selectedTitle?.title_name ?? '',
        formData.fname ?? '',
        formData.lname ?? '',
      ]
        .map((part) => String(part).trim())
        .filter(Boolean);
      const fullName = parts.join(' ');
      const headerOwnerKey = String(formData.email ?? profile?.email ?? '').trim();

      if (fullName) {
        sessionStorage.setItem(SESSION_STORAGE_KEYS.HEADER_DISPLAY_NAME, fullName);
        if (headerOwnerKey) {
          sessionStorage.setItem(SESSION_STORAGE_KEYS.HEADER_DISPLAY_NAME_OWNER, headerOwnerKey);
        }
      } else {
        sessionStorage.removeItem(SESSION_STORAGE_KEYS.HEADER_DISPLAY_NAME);
        sessionStorage.removeItem(SESSION_STORAGE_KEYS.HEADER_DISPLAY_NAME_OWNER);
      }

      window.dispatchEvent(new Event('ntcog-profile-updated'));
      setSuccess('Profile updated successfully.');
      refetch();
    } catch (err) {
      setPageError(err instanceof Error ? err.message : 'Failed to update profile.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
        <Spinner animation="border" role="status" variant="primary">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <div className="ntcog-page-title-block">
        <h1 className="ntcog-page-heading mb-1">My Profile</h1>
        <p className="text-muted mb-0">
          Manage your personal information here. System preferences now live on the settings page.
        </p>
      </div>

      {profileError ? (
        <Alert variant="danger" className="mt-4">
          {profileError}
        </Alert>
      ) : profile ? (
        <div className="ntcog-form-section mt-4">
          <div className="d-flex align-items-center gap-2 mb-3">
            <FiUser className="text-primary" />
            <h6 className="mb-0">Profile Details</h6>
          </div>

          {pageError ? (
            <Alert variant="danger" dismissible onClose={() => setPageError(null)}>
              {pageError}
            </Alert>
          ) : null}
          {success ? (
            <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          ) : null}

          <Form onSubmit={handleSave}>
            <Row className="g-3 mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-medium small">
                    Email
                    <InfoTooltip text="Your sign-in email. This cannot be changed here." />
                  </Form.Label>
                  <Form.Control type="email" value={profile.email || ''} disabled />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-medium small">
                    TRN
                    <InfoTooltip text="Your Tax Registration Number. This cannot be changed here." />
                  </Form.Label>
                  <Form.Control type="text" value={profile.trn || ''} disabled />
                </Form.Group>
              </Col>
            </Row>

            <Row className="g-3 mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-medium small">
                    Title
                    <InfoTooltip text="Your title or prefix, such as Mr, Mrs, Sis, or Rev." />
                  </Form.Label>
                  <Form.Select
                    value={formData.title_code || ''}
                    onChange={(e) => handleChange('title_code', e.target.value)}
                    disabled={saving || titlesLoading}
                  >
                    <option value="">
                      {titlesLoading ? 'Loading titles...' : 'Select a title...'}
                    </option>
                    {titles.map((title) => (
                      <option key={title.title_code} value={title.title_code}>
                        {title.title_name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-medium small">
                    First Name
                    <InfoTooltip text="Your first or given name." />
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.fname || ''}
                    onChange={(e) => handleChange('fname', e.target.value)}
                    maxLength={150}
                    disabled={saving}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-medium small">
                    Last Name
                    <InfoTooltip text="Your last or family name." />
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.lname || ''}
                    onChange={(e) => handleChange('lname', e.target.value)}
                    maxLength={150}
                    disabled={saving}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="g-3 mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-medium small">
                    Date of Birth
                    <InfoTooltip text="Your date of birth. Used for profile display only." />
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={toDateOnlyInputValue(formData.date_of_birth)}
                    onChange={(e) => handleChange('date_of_birth', e.target.value)}
                    disabled={saving}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-medium small">
                    Gender
                    <InfoTooltip text="Your gender. Used for profile display only." />
                  </Form.Label>
                  <Form.Select
                    value={formData.gender || ''}
                    onChange={(e) => handleChange('gender', e.target.value)}
                    disabled={saving}
                  >
                    <option value="">Select gender...</option>
                    <option value="M">Male</option>
                    <option value="F">Female</option>
                    <option value="O">Other</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-medium small">
                    Phone Number
                    <InfoTooltip text="A contact phone number used for profile and notifications." />
                  </Form.Label>
                  <Form.Control
                    type="tel"
                    value={formData.phone1_no || ''}
                    onChange={(e) => handleChange('phone1_no', e.target.value)}
                    maxLength={20}
                    disabled={saving}
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-end">
              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="btn-action"
                disabled={saving}
                aria-busy={saving}
              >
                {saving ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FiSave className="me-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </Form>
        </div>
      ) : (
        <div className="ntcog-form-section text-center py-5 text-muted mt-4">
          No profile information available.
        </div>
      )}
    </Container>
  );
};

export default UserProfilePage;
